/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Ronnie Wisawapakorn
 * Created: 23/11/22
 */

import {
    withCurrentAssignment,
    WithCurrentAssignmentProps,
} from '@/components/contexts/AssignmentContext/withCurrentAssignment';
import WorkplanVersionProvider from '@/components/contexts/VersionContext/WorkplanVersionProvider';
import { WorkplanIdProvider } from '@/components/contexts/WorkplanContext/providers';
import WorkplanSummaryProvider from '@/components/contexts/WorkplanSummaryContext/WorkplanSummaryProvider';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { WorkplanAllocationsTile } from '@/components/pages/dashboard/tiles';
import ItemsTile from '@/components/pages/dashboard/tiles/ItemsTile/ItemsTile';
import ReviewTile from '@/components/pages/dashboard/tiles/ReviewTile/ReviewTile';
import WorkplanLeaveTile from '@/components/pages/dashboard/tiles/LeaveTile/WorkplanLeaveTile';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import usePageTitle from '@/utils/hooks/usePageTitle';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { Flex, Grid, GridItem } from '@chakra-ui/react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { UserSupervisionWorkplanHeader } from '../HeaderTile/UserSupervisionWorkplanHeader';
import WorkplanSupervisorsTile from '@/components/pages/dashboard/tiles/SupervisorTile/WorkplanSupervisorsTile';
import { DashboardHeaderTile } from '@/components/Header/DashboardHeaderTile';
import ButtonActionTile from '@/components/pages/dashboard/tiles/ButtonTile/ButtonActionTile';
import WorkplanEOYRatingTile from '@/components/pages/dashboard/tiles/WorkplanEOYRatingTile/WorkplanEOYRatingTile';
import { MySummaryHeading } from '@/components/pages/dashboard/MySummaryHeading';

const UserSupervisionWorkplan = ({ currentAssignment }: WithCurrentAssignmentProps) => {
    usePageTitle('User Supervision Workplan');
    const { workplanId, assignmentId, version } = useParams<{
        workplanId: string;
        assignmentId: string;
        version: string;
        userDetails: string;
    }>();

    const {
        makeRequest: makeAssignmentGetRequest,
        loading: assignmentLoading,
        responseData: assignment,
        error: assignmentError,
    } = useGetRequest<Assignment>();

    useAsyncLayoutEffect(async () => {
        try {
            await getAssignment();
        } catch (error) {
            // handled by hook
        }
    }, [currentAssignment, assignmentId]);

    if (assignmentError) {
        return (
            <Flex justifyContent="center" width={'100%'}>
                <SimpleAlert title="Error" variant="subtle">
                    {`Could not load user supervision workplan`}
                </SimpleAlert>
            </Flex>
        );
    }

    if (assignmentLoading) {
        return <LoadingSpinner fullScreen={false} />;
    }

    const getAssignment = async () => {
        if (currentAssignment) {
            await makeAssignmentGetRequest(`/assignment/${assignmentId}`, {
                params: {
                    assignment: currentAssignment.id,
                },
            });
        }
    };

    return (
        <WorkplanIdProvider id={Number(workplanId && workplanId)}>
            <WorkplanVersionProvider version={version ? Number.parseInt(version) : undefined}>
                <WorkplanSummaryProvider>
                    <Grid w={'100%'} templateColumns={'repeat(2, 1fr)'} gap={6} p={6}>
                        <UserSupervisionWorkplanHeader assignment={assignment} version={version} />
                        <DashboardHeaderTile heading />
                        <GridItem colSpan={2}>
                            <MySummaryHeading />
                        </GridItem>
                        <GridItem colSpan={{ base: 2, lg: 1 }}>
                            <Grid gap={6} templateRows={'0.5fr 1.5fr'} h={'100%'}>
                                <WorkplanSupervisorsTile />
                                <WorkplanLeaveTile />
                            </Grid>
                        </GridItem>
                        <WorkplanAllocationsTile />
                        <ItemsTile />
                        <WorkplanEOYRatingTile assignment={currentAssignment} />
                        <ReviewTile assignment={currentAssignment} />
                        <ButtonActionTile assignment={currentAssignment} />
                    </Grid>
                </WorkplanSummaryProvider>
            </WorkplanVersionProvider>
        </WorkplanIdProvider>
    );
};

export default withCurrentAssignment(UserSupervisionWorkplan);
